import { Component, DestroyRef, inject, OnInit, ViewChild } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ActivatedRoute, Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { CatalogService } from 'app/Services/CatalogManagement/catalog.service';
import { SystemService } from 'app/Services/system.service';
import { CatalogComponent } from 'app/catalog/browse/browse.component';
import { CatalogTreeFooterButton } from 'app/Model/ui/CatalogTreeFooterButton';
import { JobService } from 'app/Services/job.service';
import { StartAction } from 'app/Model/Dto/StartAction';
import { GoldenRecordService } from 'app/Services/goldenRecord.service';
import {
    UserActionUpdateToCatalogComponent
} from 'app/catalog/functions/userAction/userAction-updateToCatalog/userAction-updateToCatalog.component';
import { ExportParameter } from 'app/Model/exportParameter/ExportParameter';
import { CatalogFilter } from 'app/Model/exportParameter/CatalogFilter';
import { SearchByListParameter } from 'app/Model/Dto/SearchByListParameter';
import { LoginService } from 'app/Services/login.service';
import { ImportContainerService, ComparisonCounts } from 'app/Services/DataProcess/import-container.service';
import { SignalrService } from 'app/Services/signalr.service';


type PrepareCatalogDetailsTabId = 'browse' | 'changed' | 'new' | 'deleted';

interface PrepareCatalogDetailsTabItem {
    id: PrepareCatalogDetailsTabId;
    title: string;
}

@Component({
    selector: 'np-prepare-catalog-details',
    templateUrl: './prepare-catalog-details.component.html',
    standalone: false,
    styleUrls: ['./prepare-catalog-details.component.css']
})
export class PrepareCatalogDetailsComponent implements OnInit {
    @ViewChild(CatalogComponent) private catalog: CatalogComponent;

    @ViewChild('updateToCatalogRef') private updateToCatalogRef: UserActionUpdateToCatalogComponent;

    public footerButtons: CatalogTreeFooterButton[] = [];

    public updateToCatalogPopupVisible = false;
    public updateToCatalogTargetCatalogIds: string[] = [];
    public updateToCatalogFilter: ExportParameter;

    public selectedSupplierPids: string[] = [];
    public allInStateSelected = false;

    public deletePopupVisible = false;

    public discontinuePopupVisible = false;
    public discontinueDate: Date;

    public readonly tabItems: PrepareCatalogDetailsTabItem[] = [
        { id: 'browse', title: this.translate.instant('Katalog') },
        { id: 'changed', title: this.translate.instant('Geänderte') },
        { id: 'new', title: this.translate.instant('Neue') },
        { id: 'deleted', title: this.translate.instant('Gelöschte') }
    ];

    public selectedTabId: PrepareCatalogDetailsTabId = 'browse';
    public selectedTabIndex = 0;

    public comparisonCounts: ComparisonCounts;

    public catalogTitle = '';

    public customerIdFromRoute: string;
    public catalogIdFromRoute: string;

    private readonly destroyRef = inject(DestroyRef);

    public constructor(
        private activatedRoute: ActivatedRoute,
        public systemService: SystemService,
        private catalogService: CatalogService,
        public translate: TranslateService,
        private router: Router,
        private jobService: JobService,
        private goldenRecordService: GoldenRecordService,
        private loginService: LoginService,
        private importContainerService: ImportContainerService,
        private signalrService: SignalrService
    ) {
        this.confirmDeleteProducts = this.confirmDeleteProducts.bind(this);
        this.cancelDeleteProducts = this.cancelDeleteProducts.bind(this);
        this.confirmDiscontinueProducts = this.confirmDiscontinueProducts.bind(this);
        this.cancelDiscontinueProducts = this.cancelDiscontinueProducts.bind(this);
        this.startUpdateToCatalog = this.startUpdateToCatalog.bind(this);
        this.cancelUpdateToCatalog = this.cancelUpdateToCatalog.bind(this);
    }

    public ngOnInit(): void {
        this.customerIdFromRoute = this.activatedRoute.snapshot.paramMap.get('customerId');
        this.catalogIdFromRoute = this.activatedRoute.snapshot.paramMap.get('catalogId');

        if (!this.customerIdFromRoute || !this.catalogIdFromRoute) {
            this.systemService.notifyWarning(
                this.translate.instant('Route Parameter customerId oder catalogId fehlen.')
            );
            this.router.navigate(['preparation/catalogs']);
            return;
        }

        this.catalogService.browseRoutePrefix = '/dataprocess/preparation/catalog';

        this.systemService.currentNavigationTitle = this.translate.instant('Katalog vorbereiten');

        this.catalogService
            .getCatalog(this.catalogIdFromRoute, this.customerIdFromRoute)
            .subscribe((catalog: any) => {
                this.catalogTitle = this.getCatalogTitleFromCatalog(catalog, this.catalogIdFromRoute!);
            });

        this.footerButtons = this.buildFooterButtons();
        this.loadComparisonCounts();

        this.signalrService.refreshCatalogList$
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe(() => {
                this.loadComparisonCounts();
                this.catalog?.refresh();
            });
    }

    private async loadComparisonCounts(): Promise<void> {
        this.comparisonCounts = await this.importContainerService.getComparisonCounts(
            this.catalogIdFromRoute,
            this.customerIdFromRoute
        );
    }

    public getTabCount(tabId: PrepareCatalogDetailsTabId): number | null {
        if (!this.comparisonCounts) {
            return null;
        }
        switch (tabId) {
            case 'browse':
                return this.comparisonCounts.total;
            case 'changed':
                return this.comparisonCounts.geaendert;
            case 'new':
                return this.comparisonCounts.neu;
            case 'deleted':
                return this.comparisonCounts.geloescht;
            default:
                return null;
        }
    }

    public onTabSelectionChanged(event: { addedItems?: PrepareCatalogDetailsTabItem[] }): void {
        const tab = event?.addedItems?.[0];
        if (!tab) {
            return;
        }

        this.selectedTabId = tab.id;
        this.selectedTabIndex = this.tabItems.findIndex((x) => x.id === tab.id);
        this.footerButtons = this.buildFooterButtons();
    }

    private buildFooterButtons(): CatalogTreeFooterButton[] {
        if (this.selectedTabId === 'deleted') {
            return [
                {
                    text: this.translate.instant('Löschen'),
                    icon: 'trash',
                    onClick: () => this.deleteSelectedProducts()
                },
                {
                    text: this.translate.instant('Produkte auslaufen'),
                    icon: 'clear',
                    stylingMode: 'outlined',
                    onClick: () => this.discontinueSelectedProducts()
                }
            ];
        }
        if (this.selectedTabId === 'new') {
            return [
                {
                    text: this.translate.instant('Hinzufügen zum Golden Record'),
                    icon: 'refresh',
                    onClick: () => this.updateToGoldenRecord()
                }
            ];
        }
        if (this.selectedTabId === 'changed') {
            return [
                {
                    text: this.translate.instant('Update zum Golden Record'),
                    icon: 'refresh',
                    onClick: () => this.openUpdateToCatalog()
                }
            ];
        }
        if (this.selectedTabId === 'browse') {
            return [
                {
                    text: this.translate.instant('Vergleich mit Golden Record'),
                    icon: 'detailslayout',
                    onClick: () => this.compareWithGoldenRecord()
                }
            ];
        }
        return [];
    }

    private async compareWithGoldenRecord(): Promise<void> {
        const goldenRecord = await this.goldenRecordService.getGoldenRecord(this.customerIdFromRoute);
        if (!goldenRecord?.catalogId) {
            this.systemService.notifyWarning(
                this.translate.instant('Kein Golden Record gefunden.')
            );
            return;
        }

        const startAction = new StartAction();
        startAction.catalogId = this.catalogIdFromRoute;
        startAction.customerId = this.customerIdFromRoute;
        startAction.actionId = 'CompareWithGoldenRecord';

        this.jobService.startUserAction(startAction).subscribe((jobId) => {
            if (jobId) {
                this.systemService.notifyInfo(
                    this.translate.instant('Der Vergleich mit dem Golden Record wurde gestartet.')
                );
            }
        });
    }

    private async openUpdateToCatalog(): Promise<void> {
        const goldenRecord = await this.goldenRecordService.getGoldenRecord(this.customerIdFromRoute);
        if (!goldenRecord?.catalogId) {
            this.systemService.notifyWarning(
                this.translate.instant('Kein Golden Record gefunden.')
            );
            return;
        }

        const supplierPids = this.catalog?.getSelectedProductSupplierPids() ?? [];
        if (supplierPids.length === 0) {
            this.systemService.notifyWarning(
                this.translate.instant('Bitte wählen Sie mindestens ein Produkt aus.')
            );
            return;
        }

        this.updateToCatalogTargetCatalogIds = [goldenRecord.catalogId];
        this.updateToCatalogFilter = this.buildTemporaryUpdateFilter(supplierPids);
        this.updateToCatalogPopupVisible = true;
    }

    private buildTemporaryUpdateFilter(supplierPids: string[]): ExportParameter {
        const filter = new ExportParameter();
        filter.name = 'Update zum Golden Record (temporär)';
        filter.isUpdateFilter = true;
        filter.productStatesFilter = new CatalogFilter<string[]>(['NP-Geändert']);
        filter.productStatesFilter.useFilter = true;
        filter.searchByListParameter = new CatalogFilter<SearchByListParameter>(
            new SearchByListParameter('SupplierPID', supplierPids.join('\n'))
        );
        filter.searchByListParameter.useFilter = true;
        return filter;
    }

    public async startUpdateToCatalog(): Promise<void> {
        if (this.updateToCatalogRef && !(await this.updateToCatalogRef.canStartMacro())) {
            return;
        }

        const startAction = new StartAction();
        startAction.catalogId = this.catalogIdFromRoute;
        startAction.customerId = this.customerIdFromRoute;
        startAction.actionType = 'UpdateToCatalog';
        startAction.actionParameter = this.updateToCatalogRef?.createParameterStringAsJsonString();

        this.jobService.startUserAction(startAction).subscribe((jobId) => {
            if (jobId) {
                this.systemService.notifyInfo(
                    this.translate.instant('Der Job zum Update zum Golden Record wurde gestartet.')
                );
            }
        });

        this.updateToCatalogPopupVisible = false;
    }

    public cancelUpdateToCatalog(): void {
        this.updateToCatalogPopupVisible = false;
    }

    private async updateToGoldenRecord(): Promise<void> {
        const goldenRecord = await this.goldenRecordService.getGoldenRecord(this.customerIdFromRoute);
        if (!goldenRecord?.catalogId) {
            this.systemService.notifyWarning(
                this.translate.instant('Kein Golden Record gefunden.')
            );
            return;
        }

        this.router.navigate(
            ['/dataprocess/preparation/combine', goldenRecord.catalogId, this.catalogIdFromRoute],
            { queryParams: { lockSelection: true, sourceStateFilter: 'NP-Neu' } }
        );
    }

    private captureSelection(): boolean {
        const selection = this.catalog?.getSelection() ?? { allInState: false, supplierPids: [] };
        this.allInStateSelected = selection.allInState;
        this.selectedSupplierPids = selection.supplierPids;
        if (!this.allInStateSelected && this.selectedSupplierPids.length === 0) {
            this.systemService.notifyWarning(
                this.translate.instant('Bitte wählen Sie mindestens ein Produkt aus.')
            );
            return false;
        }
        return true;
    }

    public get selectedProductCount(): number {
        return this.allInStateSelected
            ? this.getTabCount(this.selectedTabId) ?? 0
            : this.selectedSupplierPids.length;
    }

    private buildProductSelectionParameter(): object {
        return this.allInStateSelected
            ? {
                  AllInState: true,
                  SourceCatalogId: this.catalogIdFromRoute,
                  State: this.additionalFilterForBrowseComponent
              }
            : {
                  SupplierPids: this.selectedSupplierPids,
                  SourceCatalogId: this.catalogIdFromRoute,
                  State: this.additionalFilterForBrowseComponent
              };
    }

    private deleteSelectedProducts(): void {
        if (!this.captureSelection()) {
            return;
        }
        this.deletePopupVisible = true;
    }

    private discontinueSelectedProducts(): void {
        if (!this.captureSelection()) {
            return;
        }
        this.discontinueDate = null;
        this.discontinuePopupVisible = true;
    }

    public confirmDeleteProducts(): void {
        const startAction = new StartAction();
        startAction.catalogId = this.catalogIdFromRoute;
        startAction.customerId = this.customerIdFromRoute;
        startAction.actionType = 'DeleteSelectedProducts';
        startAction.actionParameter = JSON.stringify(this.buildProductSelectionParameter());

        this.jobService.startUserAction(startAction).subscribe((jobId) => {
            if (jobId) {
                this.systemService.notifyInfo(
                    this.translate.instant('Der Job zum Löschen der Produkte wurde gestartet.')
                );
            }
        });

        this.deletePopupVisible = false;
    }

    public cancelDeleteProducts(): void {
        this.deletePopupVisible = false;
    }

    public confirmDiscontinueProducts(): void {
        if (!this.discontinueDate) {
            this.systemService.notifyWarning(
                this.translate.instant('Bitte wählen Sie ein Auslaufdatum aus.')
            );
            return;
        }

        const startAction = new StartAction();
        startAction.catalogId = this.catalogIdFromRoute;
        startAction.customerId = this.customerIdFromRoute;
        startAction.actionType = 'DiscontinueSelectedProducts';
        startAction.actionParameter = JSON.stringify({
            ...this.buildProductSelectionParameter(),
            DiscontinuedDate: this.discontinueDate
        });

        this.jobService.startUserAction(startAction).subscribe((jobId) => {
            if (jobId) {
                this.systemService.notifyInfo(
                    this.translate.instant('Der Job zum Auslaufen der Produkte wurde gestartet.')
                );
            }
        });

        this.discontinuePopupVisible = false;
    }

    public cancelDiscontinueProducts(): void {
        this.discontinuePopupVisible = false;
    }

    public get isBrowseTab(): boolean {
        return this.selectedTabId === 'browse';
    }

    public get isDeletedTab(): boolean {
        return this.selectedTabId === 'deleted';
    }

    public get isSelectableTab(): boolean {
        return this.selectedTabId === 'deleted' || this.selectedTabId === 'changed';
    }

    public get additionalFilterForBrowseComponent(): string | null {
        switch (this.selectedTabId) {
            case 'changed':
                return 'NP-Geändert';
            case 'new':
                return 'NP-Neu';
            case 'deleted':
                return 'NP-Gelöscht';
            case 'browse':
            default:
                return null;
        }
    }

    public goBackToPrepareStage(): void {
        this.router.navigate(['/dataprocess/preparation']);
    }

    private getCatalogTitleFromCatalog(catalog: any, fallbackCatalogId: string): string {
        const title =
            catalog?.descriptionShort ??
            catalog?.catalogName ??
            catalog?.name ??
            catalog?.groupName ??
            catalog?.title;

        return title && String(title).trim().length > 0 ? String(title) : fallbackCatalogId;
    }
}
